// ---------------------------------------------------------------------------
// Webinar Interactive Canvas — Schema
// ---------------------------------------------------------------------------
// CRITICAL ARCHITECTURE: TenantRealtimeDO is RELAY ONLY.
// ALL canvas state lives in these tables. DO broadcasts "element added"
// events — it does NOT store elements in memory. 128MB DO limit would be
// exceeded with 1000 attendees creating stickies.
//
// Pattern: Write to DB → broadcast via DO → clients fetch from DB on connect.
// Same pattern as webinar chat messages.
//
// Canvas elements are APPEND-ONLY (CRDT-like): stickies, votes, emoji.
// No conflicts possible — each attendee's additions are independent.
// ---------------------------------------------------------------------------

import {
  pgTable,
  uuid,
  text,
  boolean,
  integer,
  numeric,
  jsonb,
  timestamp,
  index,
  uniqueIndex,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Canvas Element Types
// ---------------------------------------------------------------------------

export const CANVAS_ELEMENT_TYPES = [
  "sticky_note",        // Attendee writes + places
  "text_block",         // Presenter static text
  "poll_card",          // Vote + animated results
  "word_cloud",         // Type words → cloud builds
  "rating_scale",       // 1-10, avg shown live
  "emoji_zone",         // Drop anywhere → heat map
  // Sprint 12: 4 additional types
  "card_sorting",       // Drag cards into categories
  "priority_ranking",   // Drag-rank → averaged result
  "positioning_matrix", // X/Y axes → scatter plot
  "timer",              // Countdown visible to all
] as const;
export type CanvasElementType = (typeof CANVAS_ELEMENT_TYPES)[number];

export const CANVAS_BOARD_STATUSES = [
  "draft",
  "active",
  "archived",
] as const;
export type CanvasBoardStatus = (typeof CANVAS_BOARD_STATUSES)[number];

// ---------------------------------------------------------------------------
// Canvas Boards — one or more per webinar
// ---------------------------------------------------------------------------
// Two-canvas architecture:
//   isPresenterOnly = true  → staging canvas (only presenters see)
//   isPresenterOnly = false → viewer canvas (all attendees see)
// Presenter drags elements from staging → viewer by updating the element's boardId.

export const webinarCanvasBoards = pgTable(
  "webinar_canvas_boards",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    title: text("title").notNull(),
    isPresenterOnly: boolean("is_presenter_only").notNull().default(false),
    status: text("status").notNull().default("draft"),
    // Fog of War zones — JSONB array of rectangle overlays
    // Each zone: { id, x, y, width, height, isRevealed, label? }
    fogZones: jsonb("fog_zones").$type<FogZone[]>().notNull().default([]),
    // Canvas dimensions and zoom
    width: integer("width").notNull().default(1920),
    height: integer("height").notNull().default(1080),
    backgroundColor: text("background_color").notNull().default("#ffffff"),
    sortOrder: integer("sort_order").notNull().default(0),
    elementCount: integer("element_count").notNull().default(0),
    maxElements: integer("max_elements").notNull().default(500),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_canvas_boards_webinar").on(table.tenantId, table.webinarId),
    index("idx_canvas_boards_status").on(table.tenantId, table.status),
    check(
      "chk_canvas_board_status",
      sql`${table.status} IN ('draft', 'active', 'archived')`,
    ),
  ],
);

export interface FogZone {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isRevealed: boolean;
  label?: string;
}

// ---------------------------------------------------------------------------
// Canvas Elements — the items on the canvas
// ---------------------------------------------------------------------------
// APPEND-ONLY for attendee elements (stickies, votes, emoji).
// Presenter elements (text_block) can be updated by the presenter.
// Each element is a positioned item on a specific board.

export interface StickyNoteContent {
  text: string;
  color: string; // hex
}

export interface TextBlockContent {
  text: string;
  fontSize: number;
  fontWeight: "normal" | "bold";
  textAlign: "left" | "center" | "right";
}

export interface PollCardContent {
  question: string;
  options: Array<{ id: string; label: string; voteCount: number }>;
  isOpen: boolean;
}

export interface WordCloudContent {
  prompt: string;
  words: Array<{ text: string; count: number }>;
}

export interface RatingScaleContent {
  label: string;
  minValue: number;
  maxValue: number;
  avgRating: number;
  totalResponses: number;
}

export interface EmojiZoneContent {
  allowedEmojis: string[];
  placements: Array<{ emoji: string; x: number; y: number; count: number }>;
}

// Sprint 12: 4 new content types

export interface CardSortingContent {
  instruction: string;
  categories: Array<{ id: string; label: string }>;
  cards: Array<{ id: string; text: string; categoryId: string | null }>;
}

export interface PriorityRankingContent {
  instruction: string;
  items: Array<{ id: string; text: string }>;
  averagedRanking: Array<{ itemId: string; avgPosition: number; responseCount: number }>;
}

export interface PositioningMatrixContent {
  xAxisLabel: string;
  yAxisLabel: string;
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  dots: Array<{ respondentId: string; x: number; y: number }>;
}

export interface TimerContent {
  durationSeconds: number;
  startedAt: string | null; // ISO datetime, null = not started
  label: string;
  autoHideOnComplete: boolean;
}

export type CanvasElementContent =
  | StickyNoteContent
  | TextBlockContent
  | PollCardContent
  | WordCloudContent
  | RatingScaleContent
  | EmojiZoneContent
  | CardSortingContent
  | PriorityRankingContent
  | PositioningMatrixContent
  | TimerContent;

export const webinarCanvasElements = pgTable(
  "webinar_canvas_elements",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    boardId: uuid("board_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    elementType: text("element_type").notNull(),
    // Position on canvas (top-left corner)
    x: numeric("x", { precision: 10, scale: 2 }).notNull().default("0"),
    y: numeric("y", { precision: 10, scale: 2 }).notNull().default("0"),
    width: numeric("width", { precision: 10, scale: 2 }).notNull().default("200"),
    height: numeric("height", { precision: 10, scale: 2 }).notNull().default("150"),
    // Rotation in degrees (0-360)
    rotation: numeric("rotation", { precision: 6, scale: 2 }).notNull().default("0"),
    // Z-index for layering
    zIndex: integer("z_index").notNull().default(0),
    // Type-specific content (JSONB — schema varies by elementType)
    content: jsonb("content").$type<CanvasElementContent>().notNull(),
    // Who created this element
    createdBy: text("created_by").notNull(), // userId or "attendee:{personId}"
    // Is this a presenter element (editable) or attendee element (append-only)?
    isPresenterOwned: boolean("is_presenter_owned").notNull().default(false),
    // Soft delete for hide/remove without losing data
    isHidden: boolean("is_hidden").notNull().default(false),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_canvas_elements_board").on(table.tenantId, table.boardId),
    index("idx_canvas_elements_webinar").on(table.tenantId, table.webinarId),
    index("idx_canvas_elements_creator").on(table.tenantId, table.createdBy),
    check(
      "chk_canvas_element_type",
      sql`${table.elementType} IN ('sticky_note', 'text_block', 'poll_card', 'word_cloud', 'rating_scale', 'emoji_zone', 'card_sorting', 'priority_ranking', 'positioning_matrix', 'timer')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Canvas Votes — per-attendee votes on poll cards and rating scales
// ---------------------------------------------------------------------------
// Unique per (elementId, respondentId) — one vote per person per element.
// Used for poll_card (selectedOptionId) and rating_scale (ratingValue).

export const webinarCanvasVotes = pgTable(
  "webinar_canvas_votes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    elementId: uuid("element_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    respondentId: text("respondent_id").notNull(), // userId or personId
    // For poll_card: which option was selected
    selectedOptionId: text("selected_option_id"),
    // For rating_scale: numeric value
    ratingValue: integer("rating_value"),
    // For word_cloud: the word submitted
    wordSubmission: text("word_submission"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    // One vote per person per element
    uniqueIndex("uq_canvas_vote_element_respondent").on(
      table.elementId,
      table.respondentId,
    ),
    index("idx_canvas_votes_element").on(table.tenantId, table.elementId),
    index("idx_canvas_votes_webinar").on(table.tenantId, table.webinarId),
  ],
);
